import { inject } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot, UrlTree } from '@angular/router';
import { getAuth, isSignInWithEmailLink } from '@angular/fire/auth';

/**
 * Guard that ensures the verify-email-link page can only be accessed
 * with a valid email link and sessionId.
 *
 * Validates:
 * - sessionId query parameter exists
 * - URL contains valid Firebase email link parameters (oobCode, mode, etc.)
 *
 * Redirects to email-link-sign-in if validation fails.
 */
export const emailLinkVerificationCanActivateGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot
): boolean | UrlTree => {
  const router = inject(Router);

  // Check for sessionId in query params
  const sessionId = route.queryParams['sessionId'];
  if (!sessionId || typeof sessionId !== 'string' || sessionId.trim() === '') {
    console.warn('Email link verification guard: Missing sessionId - redirecting to email-link-sign-in');
    return router.createUrlTree(['/email-link-sign-in']);
  }

  // Check if URL contains valid email link parameters
  // Firebase email links contain: oobCode, mode, apiKey, and continueUrl
  const url = window.location.href;
  const urlParams = new URLSearchParams(window.location.search);

  // Check for required Firebase email link parameters
  const hasOobCode = urlParams.has('oobCode');
  const hasMode = urlParams.has('mode');
  const hasApiKey = urlParams.has('apiKey') || url.includes('apiKey=');

  // Also check if it's a valid email link using Firebase's validation
  // (This is a best-effort check; full validation happens in the component)
  const auth = getAuth();
  let isValidEmailLink = false;

  try {
    // isSignInWithEmailLink checks for the presence of required parameters
    isValidEmailLink = isSignInWithEmailLink(auth, url);
  } catch (error) {
    // If validation fails, we'll still check basic parameters
    console.warn('Email link verification guard: Error validating email link', error);
  }

  // Require either Firebase validation OR basic parameter presence
  if (!isValidEmailLink && !(hasOobCode && hasMode)) {
    console.warn('Email link verification guard: Invalid email link parameters - redirecting to email-link-sign-in');
    return router.createUrlTree(['/email-link-sign-in']);
  }

  // All validations passed - allow access
  return true;
};





